import {
  ApolloError,
  BaseMutationOptions,
  ServerError,
  useLazyQuery as apolloUseLazyQuery,
  useMutation as apolloUseMutation,
  useQuery as apolloUseQuery,
} from "@apollo/client";
import { UseToastProps, useToast } from "@homebound/beam";
import { useCallback, useMemo, useRef } from "react";

export { skipToken } from "@apollo/client";
export type {
  LazyQueryHookOptions,
  MutationHookOptions,
  QueryHookOptions,
  SkipToken,
  SuspenseQueryHookOptions,
} from "@apollo/client";

/** Wraps the Apollo `useQuery` with default `onError` behavior. */
export const useQuery: typeof apolloUseQuery = function (query, options) {
  const { showToast } = useToast();
  const onError = useCallback((error: ApolloError) => toastOnError("query", showToast, error), [showToast]);
  return apolloUseQuery(query, { onError, ...options });
};

/** Wraps the Apollo `useLazyQuery` with default `onError` behavior. */
export const useLazyQuery: typeof apolloUseLazyQuery = function (query, options) {
  const { showToast } = useToast();
  const onError = useCallback((error: ApolloError) => toastOnError("query", showToast, error), [showToast]);
  return apolloUseLazyQuery(query, { onError, ...options });
};

/** Wraps the Apollo `useMutation` with default `onError` behavior. */
export const useMutation: typeof apolloUseMutation = function (mutation, options) {
  const { showToast, clear } = useToast();
  const { onError: baseOnError, onCompleted: baseOnCompleted, ...restOptions } = options || {};

  const failed = useRef(false);

  const onError: (onExecutionOnError?: BaseMutationOptions["onError"]) => BaseMutationOptions["onError"] = useCallback(
    (onExecutionOnError?: BaseMutationOptions["onError"]) => (error) => {
      failed.current = true;
      if (baseOnError || onExecutionOnError) {
        baseOnError?.(error);
        onExecutionOnError?.(error);
      } else {
        toastOnError("mutation", showToast, error);
      }
    },
    [baseOnError, showToast],
  );

  const onCompleted: (
    onExecutionOnCompleted?: BaseMutationOptions["onCompleted"],
  ) => BaseMutationOptions["onCompleted"] = useCallback(
    (onExecutionOnCompleted: BaseMutationOptions["onCompleted"]) => (data, clientOptions) => {
      baseOnCompleted?.(data, clientOptions);
      onExecutionOnCompleted?.(data, clientOptions);
      if (failed) {
        clear();
        failed.current = false;
      }
    },
    [baseOnCompleted, clear],
  );

  const result = apolloUseMutation(mutation, { onError: onError(), onCompleted: onCompleted(), ...restOptions });

  const originalExecute = useMemo(() => result[0], [result]);
  result[0] = useCallback(
    async (options) => {
      return await originalExecute({
        ...options,
        onCompleted: onCompleted(options?.onCompleted),
        onError: onError(options?.onError),
      });
    },
    [onCompleted, onError, originalExecute],
  );

  return result as any;
};

type GraphQLError = { message: string; locations: unknown; path: unknown };

function toastOnError(type: "query" | "mutation", showToast: UseToastProps["showToast"], error: ApolloError): void {
  const result = (error.networkError as ServerError)?.result as any;
  const errors = result?.errors as GraphQLError[] | undefined;

  let message = !result || !errors || errors.length === 0 ? error.message : errors.map((e) => e.message).join(", ");

  if (message.startsWith("Fetch failed calling") && type === "mutation") {
    message =
      "The operation timed out, but may actually succeed, so please reload the page or wait before trying again.";
  }

  showToast({ type: "error", message });
  console.error(message);

  if (type === "mutation") {
    throw error;
  }
}
